import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebBaseViewComponent } from '@web/widgets/common/abstract/web-base-view.component';
import { getStyles } from './catalog-products-view.component.styles';
import { catchError, combineLatest, distinctUntilChanged, of, type Observable } from 'rxjs';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import { type SafeHtml } from '@angular/platform-browser';
import {
    EButtonAdditionalEffectsCondition,
    EButtonAdditionalHoverEffectsTypes,
    EButtonAdditionalStaticEffectsTypes,
    EButtonStyleType,
    ESize,
} from '@common/enums';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';
import { map, switchMap, take } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { CatalogProductCardComponent } from './catalog-product-card/catalog-product-card.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import {
    CatalogCategoriesView,
    type CatalogCategory,
    type CatalogProductCard,
    type CatalogProductsWidgetData,
} from '@web/widgets/catalog-products/type';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { ProductCatalogHttpService } from '@web/store/website/product-catalog/product-catalog.http.service';
import { isTariffExpiredError } from '@web/store/website/product-catalog/product-catalog.helper';
import { ProductCatalogFacade } from '@web/store/website/product-catalog/product-catalog.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { CatalogEnabledCategoriesService } from '@web/widgets/catalog-products/service/catalog-enabled-categories.service';
import {
    collectEnabledCategoryIds,
    type EnabledCategoryIds,
    filterEnabledCategories,
    filterProductsByEnabledCategories,
    findCategoryById,
    hasEnabledCategories,
    isCategoryEnabled,
    isSubtreeFullyEnabled,
} from '@web/widgets/catalog-products/utils/enabled-categories.util';
import { MAX_FILTERED_PRODUCTS } from '@web/widgets/catalog-products/constants/defaults-catalog-products.const';

@Component({
    selector: 'web-product-card-view',
    templateUrl: './catalog-products-view.component.html',
    styleUrls: ['./catalog-products-view.component.less'],
    standalone: true,
    imports: [CatalogProductCardComponent, TooltipModule, CommonModule, CommonElementsModule],
})
export class CatalogProductsViewComponent extends WebBaseViewComponent<CatalogProductsWidgetData> implements OnInit {
    protected getStyles = getStyles;
    protected readonly settingsFacade: SettingsFacade = inject(SettingsFacade);
    protected readonly productCatalogHttpService = inject(ProductCatalogHttpService);
    protected readonly productCatalogFacade = inject(ProductCatalogFacade);
    protected readonly layoutFacade = inject(LayoutFacade);
    private readonly enabledCategoriesService = inject(CatalogEnabledCategoriesService);
    private readonly destroyRef = inject(DestroyRef);

    public readonly cards = signal<CatalogProductCard[]>([]);
    public readonly total = signal(0);
    public readonly categories = signal<CatalogCategory[]>([]);
    public readonly enabledCategoryIds = signal<EnabledCategoryIds>(null);
    /**
     * The category the preview shows as active — the one pinned in the widget settings.
     * Without a pin «All categories» stays active and every published category is shown.
     */
    public readonly activeCategory = signal<number | null>(null);
    /** Until the enabled categories are known nothing may be rendered, so the loader stays. */
    public readonly enabledCategoriesLoaded = signal(false);
    public readonly loading = signal(false);

    /** A failed products request keeps the widget in its empty state, the reason is explained in the control panel */
    private readonly hasProductsError = signal(false);

    public readonly shopCurrency$: Observable<string> = this.settingsFacade.shopCurrency$;

    public readonly sortingOptions = [
        'sorting_name_asc',
        'sorting_name_desc',
        'sorting_prices_asc',
        'sorting_prices_desc',
        'sorting_date_asc',
        'sorting_date_desc',
        'sorting_default',
    ];

    getIndent(level: number): string {
        return '\u00A0'.repeat(level * 2);
    }

    ngOnInit() {
        super.ngOnInit();

        this.loadAndSetCategories();
        this.loadEnabledCategoryIds();
        this.widgetProductsSubscription();
        this.layoutFacade.setIsHideColumnControls(true);
    }

    protected widgetCallback = (): void => {
        this.updateActiveCategory();
        this.updateCards();
        this.updateCategories();
    };

    public isCategorySelectable(category: CatalogCategory): boolean {
        return isCategoryEnabled(category, this.enabledCategoryIds());
    }

    /** "All categories" stands for every category the store publishes, so one of them is enough. */
    public hasEnabledCategories(): boolean {
        return hasEnabledCategories(this.enabledCategoryIds());
    }

    /**
     * Products of a parent category also come from its children, so the ones that belong
     * to a disabled child have to be dropped before rendering.
     */
    private updateCards(): void {
        const data = this.widgetData;
        if (!data) {
            return;
        }

        if (this.hasProductsError() || !this.hasEnabledCategories()) {
            this.cards.set([]);
            this.total.set(0);

            return;
        }

        const allowedCategoryIds = collectEnabledCategoryIds(
            data.categories ?? [],
            this.enabledCategoryIds(),
            this.activeCategory()
        );

        const products = data.products ?? [];
        const enabledProducts = filterProductsByEnabledCategories(products, allowedCategoryIds);

        this.cards.set(enabledProducts.slice(0, data.maxPerPage));
        this.total.set(enabledProducts.length === products.length ? (data.total ?? 0) : enabledProducts.length);
    }

    private updateCategories(): void {
        const data = this.widgetData;
        if (!data) {
            return;
        }

        const enabledIds = this.enabledCategoryIds();
        const allCategories = filterEnabledCategories(data.categories ?? [], enabledIds);

        const selectedCategory = data.selectedCategory ?? null;
        if (selectedCategory === null) {
            this.categories.set(allCategories);
            return;
        }

        const category = findCategoryById(allCategories, selectedCategory);
        if (!category) {
            this.categories.set([]);
            return;
        }

        this.categories.set(isCategoryEnabled(category, enabledIds) ? [category] : (category.children ?? []));
    }

    private loadEnabledCategoryIds(): void {
        this.enabledCategoriesService.enabledCategoryIds$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((categoryIds) => {
                this.enabledCategoryIds.set(categoryIds);
                this.enabledCategoriesLoaded.set(true);
                this.updateActiveCategory();
                this.updateCards();
                this.updateCategories();
            });
    }

    /** Runs on every widget change as well: the category tree arrives after the enabled ids. */
    private updateActiveCategory(): void {
        this.activeCategory.set(this.widgetData?.selectedCategory ?? null);
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }

    public get nextPageButtonEffectClass(): string {
        const nextPageButton = this.widgetData?.pagination?.nextPage;
        let nameEffect: string = '';

        if (nextPageButton?.additionalEffects?.isActive) {
            if (nextPageButton?.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.hover) {
                switch (nextPageButton?.additionalEffects?.hoverEffects) {
                    case EButtonAdditionalHoverEffectsTypes.bounce:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.bounce;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.shake:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.shake;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.decrease:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.decrease;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.increase:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.increase;
                        break;
                    default:
                        break;
                }
            }

            if (nextPageButton.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.static) {
                switch (nextPageButton?.additionalEffects?.staticEffects) {
                    case EButtonAdditionalStaticEffectsTypes.shine:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.shine;
                        break;
                    case EButtonAdditionalStaticEffectsTypes.twinkle:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.twinkle;
                        break;
                    default:
                        break;
                }
            }

            return 'button_effect_' + nameEffect;
        }

        return '';
    }

    public get nextButtonStyles(): string {
        const nextPageButton = this.widgetData.pagination.nextPage;
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[nextPageButton.size ? nextPageButton.size : ESize.M]}`;

        if (nextPageButton.styleType === EButtonStyleType.custom) {
            return `${styles} ${this.classes?.nextButtonStyles} ${this.nextPageButtonEffectClass}`;
        }
        return `${styles} ${BUTTON_TYPE_CLASS_MAP[nextPageButton.styleType]} ${this.classes?.nextButtonStyles} ${
            this.nextPageButtonEffectClass
        }`;
    }

    public get filterSettings(): CatalogProductsWidgetData['filter'] {
        return this.widgetData.filter;
    }

    protected readonly CatalogCategoriesView = CatalogCategoriesView;

    /**
     * Products are requested once the enabled categories are known: the filter drops products on the
     * client, so a whole page of them has to be fetched at once instead of relying on the server side one.
     */
    private widgetProductsSubscription() {
        this.loading.set(true);

        combineLatest([this.enabledCategoriesService.enabledCategoryIds$, this.widget$])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map(([enabledIds, widget]) => ({
                    enabledIds,
                    categoryId: widget?.data?.selectedCategory ?? null,
                })),
                distinctUntilChanged(
                    (previous, current) =>
                        previous.enabledIds === current.enabledIds && previous.categoryId === current.categoryId
                ),
                switchMap(({ enabledIds, categoryId }) => {
                    // Nothing is published for this site, so there is nothing to request
                    if (!hasEnabledCategories(enabledIds)) {
                        return of(null);
                    }

                    const isFilterActive = !isSubtreeFullyEnabled(
                        this.widgetData?.categories ?? [],
                        enabledIds,
                        categoryId
                    );

                    return this.productCatalogHttpService
                        .getAllCatalogProducts({
                            categoryId,
                            offset: 0,
                            limit: isFilterActive ? MAX_FILTERED_PRODUCTS : this.widgetData?.maxPerPage,
                        })
                        .pipe(catchError((error: HttpErrorResponse) => of(error)));
                })
            )
            .subscribe((response) => {
                this.loading.set(false);

                if (response === null) {
                    this.hasProductsError.set(false);
                    this.cards.set([]);
                    this.total.set(0);

                    return;
                }

                if (response instanceof HttpErrorResponse) {
                    this.hasProductsError.set(true);
                    this.cards.set([]);
                    this.total.set(0);
                    this.productCatalogFacade.setTariffExpired(isTariffExpiredError(response));

                    return;
                }

                this.hasProductsError.set(false);
                this.productCatalogFacade.setTariffExpired(false);
                this.updateWidgetData({ products: response.products, total: response.total });
            });
    }

    private loadAndSetCategories() {
        this.productCatalogHttpService
            .getUserCategories()
            .pipe(
                take(1),
                catchError(() => of([] as CatalogCategory[])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((categories) => {
                this.updateWidgetData({ categories });
            });
    }
}
